import React, { useEffect, useState, useRef } from 'react';
import { Animated, Easing, View } from 'react-native';
import { useRouter, useSegments } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { profileService } from '@/services/profileService';
import { OnboardingStateService } from '@/services/onboardingStateService';

export default function AuthGuard({ children }: { children: React.ReactNode }) {
  const { user, loading, isInitializing, enhancedLoading } = useAuth();
  const segments = useSegments();
  const router = useRouter();
  const [initializing, setInitializing] = useState(true);
  const [checkingOnboarding, setCheckingOnboarding] = useState(false);
  const [hasValidatedUser, setHasValidatedUser] = useState(false);
  const [checkingValidation, setCheckingValidation] = useState(true);
  
  // Rotating logo animation
  const rotateAnim = useRef(new Animated.Value(0)).current;
  // Fade-out animation for loading screen
  const fadeAnim = useRef(new Animated.Value(1)).current;
  
  // Track previous segments to detect true navigation vs transient empty states
  const prevSegmentsRef = useRef<string[]>([]);
  const hasNavigatedRef = useRef(false);

  // Check if user has completed onboarding
  const checkOnboardingStatus = async (userId: string) => {
    try {
      setCheckingOnboarding(true);
      const result = await profileService.checkOnboardingStatus();

      console.log('🔍 Onboarding status check via API:', {
        completed: result.completed
      });

      return result.completed;
    } catch (error) {
      console.error('❌ Error checking onboarding status:', error);
      return false;
    } finally {
      setCheckingOnboarding(false);
    }
  };

  // Start rotating animation when component mounts
  useEffect(() => {
    const rotateAnimation = Animated.loop(
      Animated.timing(rotateAnim, {
        toValue: 1,
        duration: 2000, // 2 seconds for full rotation
        easing: Easing.linear,
        useNativeDriver: true,
      })
    );
    rotateAnimation.start();
    
    return () => rotateAnimation.stop();
  }, []);

  // Check for validated user info on mount
  // CRITICAL FIX: This must be FAST and non-blocking - no network calls here
  // Session recovery is handled by AuthContext, not AuthGuard
  useEffect(() => {
    const checkValidatedUser = async () => {
      try {
        // REMOVED: Pending session recovery moved to AuthContext
        // This was causing network calls that blocked UI rendering
        // AuthContext handles session restoration with proper timeouts
        
        const stored = await AsyncStorage.getItem('validated_user_info');
        if (stored) {
          const userInfo = JSON.parse(stored);
          // Check if validation is recent (within last 10 minutes)
          const isRecent = userInfo.validatedAt && (Date.now() - userInfo.validatedAt) < 600000;
          setHasValidatedUser(isRecent);
        }
      } catch (error) {
        console.log('⚠️ Error checking validated user:', error);
      } finally {
        // CRITICAL: Always set to false immediately - don't block UI
        setCheckingValidation(false);
      }
    };

    // Run immediately - this should be <50ms since it's just reading AsyncStorage
    checkValidatedUser();
  }, []);

  // Calculate route variables outside useEffect so they can be used in loading check
  const currentRoute = segments[0] || '';
  const inAuthGroup = segments[0] === '(auth)';
  const isWelcome = segments[0] === 'welcome';
  const isLogin = segments[0] === 'login';
  const isForgotPassword = segments[0] === 'forgot-password';
  const isFirstAccess = segments[0] === 'first-access';
  const isVerifyCode = segments[0] === 'verify-code';
  const isSetPassword = segments[0] === 'set-password';
  const isMagicLinkLogin = segments[0] === 'magic-link-login';
  const isPatientWelcome = segments[0] === 'patient-welcome';
  const isResetPassword = segments[0] === 'reset-password';
  const isOnboarding = segments[0]?.startsWith('onboarding-');
  const isOnboardingSuccess = segments[0] === 'onboarding-success';
  const isIndex = !segments.length || currentRoute === '';
  const isTabsRoute = segments[0] === '(tabs)';
  const isMenuRoute = segments[0] === 'menu';
  const isEmailSuccess = segments[0] === 'email-success';

  useEffect(() => {

    // Task #60: Routing away from auth/welcome routes only needs `user` to be
    // set — profile data can finish loading after we land on /(tabs). Don't
    // gate navigation on `enhancedLoading` when the user is on an auth route
    // with a session, otherwise a slow profile fetch traps them on /login
    // after a successful biometric prompt.
    const isAuthRouteWithUser =
      !!user && (isLogin || isWelcome || isForgotPassword || isFirstAccess ||
                 isVerifyCode || isSetPassword || isMagicLinkLogin ||
                 isResetPassword || isEmailSuccess || isIndex);
    const shouldWaitForEnhanced = enhancedLoading && !isAuthRouteWithUser;

    // CRITICAL: Wait for auth initialization to complete before making routing decisions
    // This prevents premature redirects during cold starts when Supabase connection is warming up
    if (loading || isInitializing || checkingValidation || shouldWaitForEnhanced) {
      console.log('⏳ AuthGuard: Waiting for initialization...', {
        loading,
        isInitializing,
        checkingValidation,
        enhancedLoading,
        shouldWaitForEnhanced,
        isAuthRouteWithUser,
      });
      return;
    }

    // Detect segment transitions
    const prevSegments = prevSegmentsRef.current;
    const segmentsChanged = JSON.stringify(prevSegments) !== JSON.stringify(segments);
    const isTransitioning = !segments.length && prevSegments.length > 0 && hasNavigatedRef.current;
    
    console.log('🔍 AuthGuard: Segment analysis:', {
      current: segments,
      previous: prevSegments,
      changed: segmentsChanged,
      isTransitioning,
      hasNavigated: hasNavigatedRef.current
    });

    console.log('🔍 AuthGuard: User status:', {
      hasUser: !!user,
      email: user?.email,
      currentRoute,
      inAuthGroup,
      isWelcome,
      isLogin,
      isForgotPassword,
      isFirstAccess,
      isVerifyCode,
      isSetPassword,
      isMagicLinkLogin,
      isPatientWelcome,
      isResetPassword,
      isOnboarding,
      isOnboardingSuccess,
      isIndex,
      isTabsRoute,
      isMenuRoute,
      isEmailSuccess
    });

    const handleNavigation = async () => {
      try {
        // CRITICAL FIX: Detect true navigation transitions
        // During navigation (e.g., onboarding-gender → first-access), segments briefly becomes []
        // We skip navigation logic ONLY during this transient state
        if (isTransitioning) {
          console.log('⏳ AuthGuard: Skipping navigation (transition state detected)');
          // Don't update prevSegmentsRef during transition
          return;
        }
        
        // Update previous segments for next comparison
        prevSegmentsRef.current = [...segments];
        if (segments.length > 0) {
          hasNavigatedRef.current = true;
        }

        if (!user) {
          // User is not authenticated
          // Only redirect to welcome if user is on index or on a protected route
          // Allow access to: welcome, login, forgot-password, first-access, verify-code, set-password, magic-link-login, reset-password, email-success, onboarding pages (NEW FLOW)
          const isAllowedUnauthenticatedRoute = isWelcome || isLogin || isForgotPassword || isFirstAccess || isVerifyCode || isSetPassword || isMagicLinkLogin || isResetPassword || isEmailSuccess || isOnboarding;
          
          if (isIndex || !isAllowedUnauthenticatedRoute) {
            console.log('🔄 AuthGuard: Redirecting to welcome (no user)');
            router.replace('/welcome');
          }
        } else {
          // User is authenticated
          
          // FIRST: Handle onboarding routes - allow access without validation (NEW FLOW)
          // NOTE: isOnboardingSuccess is NOT included here - we want to allow navigation FROM success page
          if (isOnboarding) {
            // Onboarding pages are accessible to authenticated users during new flow
            console.log('✅ AuthGuard: Allowing authenticated user access to onboarding page (new flow)');
            setInitializing(false);
            return;
          }

          // Skip navigation if user is already on tabs, menu, onboarding-success, or other protected routes  
          if (isTabsRoute || isMenuRoute || isOnboardingSuccess || (!isIndex && !isWelcome && !isLogin && !isForgotPassword && !isFirstAccess && !isVerifyCode && !isSetPassword && !isMagicLinkLogin && !isResetPassword)) {
            console.log('✅ AuthGuard: User already on protected route, no navigation needed');
            setInitializing(false);
            return;
          }

          // Only redirect from auth/welcome pages and index
          if (isIndex || isWelcome || isLogin || isForgotPassword || isFirstAccess || isVerifyCode || isSetPassword || isMagicLinkLogin || isResetPassword) {
            // A password-only response is not a successful signup. This
            // account-scoped hint survives a cold start and never affects
            // unrelated returning users, sponsors or administrators.
            if (await OnboardingStateService.isAuthenticatedPending(user.id).catch(() => false)) {
              let completed = false;
              try { completed = await profileService.getOnboardingCompletionStrict(); }
              catch { /* Known unfinished account: keep draft and retry from onboarding. */ }
              if (!completed) {
                await OnboardingStateService.setOnboardingInProgress(false);
                router.replace('/onboarding-welcome');
                setInitializing(false);
                return;
              }
              await OnboardingStateService.setAuthenticatedPending(user.id, false).catch(() => {});
            }
            // CRITICAL: Check if user just completed password setup (first-time signup)
            const onboardingInProgress = await OnboardingStateService.isOnboardingInProgress();
            
            if (onboardingInProgress) {
              console.log('🎯 AuthGuard: First-time signup detected - routing to onboarding-success page');
              router.replace('/onboarding-success');
              setInitializing(false);
              return;
            }
            
            // PRD_GOOGLE_AUTH — Google users authenticate BEFORE onboarding, so
            // the email-flow `validated_user_info` marker (hasValidatedUser) is
            // never set for them. AuthGuard is the single routing authority for
            // the Google path (welcome.tsx does not navigate). Classify with the
            // strict check and treat an unknown result as "complete" so a
            // transient error sends a RETURNING user home rather than back
            // through onboarding.
            const isGoogleUser = user.app_metadata?.provider === 'google';
            if (isGoogleUser) {
              let googleCompleted = true;
              try {
                googleCompleted = await profileService.getOnboardingCompletionStrict();
              } catch (statusErr) {
                console.warn('⚠️ AuthGuard: Google onboarding status check failed, routing home:', statusErr instanceof Error ? statusErr.message : String(statusErr));
                googleCompleted = true;
              }
              if (googleCompleted) {
                console.log('🔄 AuthGuard: Returning Google user → home');
                router.replace('/diary');
              } else {
                console.log('🔄 AuthGuard: New Google user → onboarding welcome');
                router.replace('/onboarding-welcome');
              }
              setInitializing(false);
              return;
            }

            // Check if user completed onboarding
            const onboardingCompleted = await checkOnboardingStatus(user.id);
            if (!onboardingCompleted) {
              // Only redirect to onboarding-welcome if user has completed set-password flow
              if (hasValidatedUser) {
                console.log('🔄 AuthGuard: Redirecting to onboarding welcome (validated user, not completed)');
                router.replace('/onboarding-welcome');
              } else {
                console.log('🔄 AuthGuard: User not validated for onboarding, redirecting to home');
                router.replace('/diary');
              }
            } else {
              console.log('🔄 AuthGuard: Redirecting authenticated user to home');
              router.replace('/diary');
            }
          } else {
            // Check onboarding for other protected routes
            const onboardingCompleted = await checkOnboardingStatus(user.id);
            if (!onboardingCompleted) {
              if (hasValidatedUser) {
                console.log('🔄 AuthGuard: Redirecting to onboarding welcome (accessing protected route)');
                router.replace('/onboarding-welcome');
              } else {
                console.log('🔄 AuthGuard: User not validated, allowing access to protected route');
              }
            }
          }
        }
      } catch (error) {
        console.error('❌ AuthGuard: Navigation error:', error);
        // Fallback navigation
        if (user) {
          router.replace('/diary');
        } else {
          router.replace('/welcome');
        }
      } finally {
        setInitializing(false);
      }
    };

    // Add a small delay to ensure auth state is fully settled
    const navigationTimer = setTimeout(() => {
      handleNavigation();
    }, 100);

    return () => clearTimeout(navigationTimer);
  }, [user, loading, segments, router, checkingValidation, enhancedLoading]);


  // Add timeout for loading states to prevent infinite loading (SKIP for onboarding and auth routes)
  useEffect(() => {
    // NEVER apply timeout logic to onboarding routes or auth flow pages - let them render immediately
    // This includes verify-code, set-password, first-access, etc.
    const isAuthFlow = isOnboarding || isVerifyCode || isSetPassword || isFirstAccess || 
                      isMagicLinkLogin || isResetPassword || isPatientWelcome || isLogin || 
                      isForgotPassword || isEmailSuccess;
                      
    if (isAuthFlow) {
      return;
    }
    
    if (loading || initializing || checkingOnboarding || checkingValidation || enhancedLoading) {
      const loadingTimeout = setTimeout(() => {
        console.log('⚠️ AuthGuard: Loading timeout - forcing completion and continuing navigation');
        setInitializing(false);
        setCheckingOnboarding(false);
        setCheckingValidation(false);
        
        // Emergency navigation to prevent blank page
        if (user) {
          console.log('🚨 AuthGuard: Emergency navigation to tabs for authenticated user');
          router.replace('/diary');
        } else {
          console.log('🚨 AuthGuard: Emergency navigation to welcome for unauthenticated user');
          router.replace('/welcome');
        }
      }, 15000); // 15 second timeout

      return () => clearTimeout(loadingTimeout);
    }
    
    // Return empty cleanup function when no timeout is set
    return () => {};
  }, [loading, initializing, checkingOnboarding, checkingValidation, enhancedLoading, user, router, isOnboarding, 
      isVerifyCode, isSetPassword, isFirstAccess, isMagicLinkLogin, isResetPassword, 
      isPatientWelcome, isLogin, isForgotPassword, isEmailSuccess]);

  // Task #60: Last-resort safety timeout for the login route. The main timeout
  // above explicitly skips auth flows so they can render their own UI freely,
  // but we still need to make sure that if a SIGNED_IN event fires while the
  // user is on /login (biometric or password), we never get trapped here
  // indefinitely waiting for profile data. If `user` is set and we're still
  // on /login after 8 seconds, force navigation to /(tabs).
  useEffect(() => {
    if (!isLogin || !user) return;

    const loginStuckTimeout = setTimeout(() => {
      console.log('⚠️ AuthGuard: Login route stuck with user set — forcing navigation to /(tabs)');
      router.replace('/diary');
    }, 8000);

    return () => clearTimeout(loginStuckTimeout);
  }, [isLogin, user, router]);

  // Show loading screen during initialization (with rotating logo)
  const shouldShowLoadingScreen = hasValidatedUser && 
                                 (isSetPassword || (!isOnboarding && (loading || initializing || checkingOnboarding || checkingValidation || isInitializing || enhancedLoading)));
                                 
  const currentPath = segments.join('/');
  
  // Rotate interpolation
  const rotate = rotateAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '360deg'],
  });
  
  // Trigger fade-out when loading completes
  useEffect(() => {
    if (!shouldShowLoadingScreen && !isInitializing) {
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }).start();
    } else {
      // Reset fade when showing loading screen
      fadeAnim.setValue(1);
    }
  }, [shouldShowLoadingScreen, isInitializing]);
  
  // Show minimal loading screen with rotating logo during cold start
  if (shouldShowLoadingScreen || isInitializing) {
    console.log('⚠️ SHOWING LOADING SCREEN:', { 
      currentPath, 
      isInitializing, 
      loading, 
      initializing, 
      shouldShowLoadingScreen 
    });
    return (
      <Animated.View style={{ 
        flex: 1, 
        backgroundColor: '#F5F0E8', 
        justifyContent: 'center', 
        alignItems: 'center',
        opacity: fadeAnim
      }}>
        <Animated.Image
          source={require('@/assets/images/elo-avatar.png')}
          style={{
            width: 56,
            height: 56,
            transform: [{ rotate }]
          }}
          resizeMode="contain"
        />
      </Animated.View>
    );
  }
  
  // For onboarding routes, ALWAYS render children regardless of loading states
  console.log('🚀 RENDERING CHILDREN for route:', currentPath, 'isOnboarding:', isOnboarding);

  return <View style={{ flex: 1 }}>{children}</View>;
}